import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { isApiSuccess } from "../utils/apiResponse";
import { getDashboardBuckets, getLeads, flattenBuckets, toLeadCard } from "../api/dashboardApi";

const SEARCH_DEBOUNCE_MS = 500;

// Dashboard tiles (counts + their status_id/sub_status_id filters) and the
// paginated, filtered/searched lead listing shown below them.
export default function useDashboardLeads() {
  const location = useLocation();
  const [dashboardBuckets, setDashboardBuckets] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  // q is the raw input (kept instant so typing stays responsive); only the
  // trimmed, debounced copy drives get_leads — one request once the user
  // pauses typing, not one per keystroke (or per added/removed space).
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(q.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [q]);
  // get_leads waits for the buckets — its status/sub-status filters come from
  // them, so firing before they land is a wasted request that's immediately
  // repeated. Set even on failure so the listing still loads.
  const [bucketsLoaded, setBucketsLoaded] = useState(false);

  useEffect(() => {
    if (location.pathname !== "/dashboard") return;
    getDashboardBuckets()
      .then(({ data }) => {
        if (!isApiSuccess(data)) {
          console.error("dashboard buckets fetch failed", data?.message);
          return;
        }
        setDashboardBuckets(flattenBuckets(data.data?.buckets_data));
      })
      .catch((err) => {
        console.error("dashboard buckets fetch failed", err.response?.status, err.response?.data || err.message);
      })
      .finally(() => setBucketsLoaded(true));
  }, [location.pathname]);

  // Match buckets by their own (status_id, sub_status_id) rather than array
  // position — sfa_dashboard prepends a role-only "Task" section (My Leads /
  // Reportee Applications) for TH/SM/SO, which would otherwise shift a
  // positional index 0/1/2/3 and silently swap in the wrong bucket (and its
  // own count_scope/request_type) for these 3 tiles.
  const tileBuckets = useMemo(() => {
    const findBucket = (statusId, subStatusId) =>
      dashboardBuckets.find((b) => {
        const fp = b?.filter_params;
        return (
          fp?.status_id?.length === 1 &&
          fp.status_id[0] === statusId &&
          fp?.sub_status_id?.length === 1 &&
          fp.sub_status_id[0] === subStatusId
        );
      });

    // status_id: 1 Application(sub 1 = My Leads/just created, sub 2 = submitted)
    // · 2 Sent to Financier · 3 Financier Approval · 4 Contract Signed
    const myLeads = findBucket(1, 1);
    const submitApplication = findBucket(1, 2);
    const approvalInProcess = findBucket(2, 3);
    const noaReceived = findBucket(3, 4);
    const contractSigned = findBucket(4, 5);
    // SM-only (visible_roles restricts this bucket server-side, so it's simply
    // absent from dashboardBuckets for every other role) — status_id spans the
    // whole pipeline with no sub_status_id, so match by its own request_type
    // rather than a (status_id, sub_status_id) pair like the others.
    const reporteeApplications = dashboardBuckets.find((b) => b?.filter_params?.request_type === "reportee_application");
    const inBankItems = [approvalInProcess, noaReceived, contractSigned].filter(Boolean);
    const allItems = [myLeads, submitApplication, approvalInProcess, noaReceived, contractSigned].filter(Boolean);

    // Always show our own fixed tile labels — never the backend bucket's own
    // label (e.g. "Submited Application", "Approval in Process"), which is
    // meant for other screens and would only confuse which tile is which.
    const tile = (item, label) => ({
      label,
      count: item?.count ?? 0,
      statusId: item?.filter_params?.status_id || [],
      subStatusId: item?.filter_params?.sub_status_id || [],
      requestType: item?.filter_params?.request_type || null,
    });

    return {
      "Lead Created": tile(myLeads, "Lead Created"),
      "Application Created": tile(submitApplication, "Application Created"),
      "In Bank Process": {
        label: "In Bank Process",
        count: inBankItems.reduce((sum, it) => sum + (it.count || 0), 0),
        statusId: [...new Set(inBankItems.flatMap((it) => it.filter_params?.status_id || []))],
        subStatusId: [...new Set(inBankItems.flatMap((it) => it.filter_params?.sub_status_id || []))],
        requestType: null,
      },
      // "All" is the union of exactly these 3 tiles — not literally every
      // lead (that would also pull in Disbursed/Lost/etc., which have no tile
      // here at all).
      all: {
        statusId: [...new Set(allItems.flatMap((it) => it.filter_params?.status_id || []))],
        subStatusId: [...new Set(allItems.flatMap((it) => it.filter_params?.sub_status_id || []))],
      },
      // Absent (null) for any role other than SM — DashboardPage only renders
      // this 4th tile when it's actually present.
      "Reportee Applications": reporteeApplications ? tile(reporteeApplications, "Reportee Applications") : null,
    };
  }, [dashboardBuckets]);

  // get_leads pages at a fixed 6-per-page server-side — this tracks the
  // current page + whether another one exists so the list can be extended
  // (see loadMoreLeads) instead of only ever showing the first 6 rows.
  const [leadsPage, setLeadsPage] = useState(1);
  const [hasMoreLeads, setHasMoreLeads] = useState(false);
  const [loadingMoreLeads, setLoadingMoreLeads] = useState(false);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const leadsCriteriaRef = useRef({ statusId: [], subStatusId: [], search: "" });
  // Repeated/rapid tile clicks (or a slow request racing a faster later one)
  // can make responses land out of order — only the most-recently-issued
  // request is allowed to update the list, so a stale response can never
  // overwrite a newer one.
  const requestSeqRef = useRef(0);
  // Criteria of the last page-1 request, so an effect re-run that lands on the
  // exact same filters (a buckets refetch rebuilding tileBuckets, etc.) is
  // skipped. Cleared on leaving the dashboard so coming back always refreshes.
  const lastCriteriaKeyRef = useRef(null);

  useEffect(() => {
    if (location.pathname !== "/dashboard") {
      lastCriteriaKeyRef.current = null;
      return;
    }
    if (!bucketsLoaded) return;
    const active = filter !== "all" ? tileBuckets[filter] : tileBuckets.all;
    const criteria = { statusId: active?.statusId, subStatusId: active?.subStatusId, requestType: active?.requestType, search: debouncedSearch };
    const criteriaKey = JSON.stringify(criteria);
    if (criteriaKey === lastCriteriaKeyRef.current) return;
    lastCriteriaKeyRef.current = criteriaKey;
    leadsCriteriaRef.current = criteria;
    const mySeq = ++requestSeqRef.current;
    setLeadsLoading(true);
    getLeads({ ...criteria, pageNum: 1 })
      .then(({ data }) => {
        if (mySeq !== requestSeqRef.current) return; // a newer request has already superseded this one
        if (!isApiSuccess(data)) {
          console.error("get_leads failed", data?.message);
          setLeads([]);
          setHasMoreLeads(false);
          setLeadsTotal(0);
          return;
        }
        const rows = Array.isArray(data.data?.lead_info) ? data.data.lead_info : [];
        setLeads(rows.map(toLeadCard));
        setLeadsPage(1);
        setHasMoreLeads(!!data.nextpage);
        setLeadsTotal(data.totalrecords ?? rows.length);
      })
      .catch((err) => {
        if (mySeq !== requestSeqRef.current) return;
        console.error("get_leads failed", err.response?.status, err.response?.data || err.message);
        setLeads([]);
        setHasMoreLeads(false);
        setLeadsTotal(0);
      })
      .finally(() => {
        if (mySeq === requestSeqRef.current) setLeadsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, bucketsLoaded, filter, debouncedSearch, tileBuckets]);

  const loadMoreLeads = () => {
    if (loadingMoreLeads || !hasMoreLeads) return;
    const nextPage = leadsPage + 1;
    const mySeq = ++requestSeqRef.current;
    setLoadingMoreLeads(true);
    getLeads({ ...leadsCriteriaRef.current, pageNum: nextPage })
      .then(({ data }) => {
        if (mySeq !== requestSeqRef.current) return;
        if (!isApiSuccess(data)) {
          console.error("get_leads (load more) failed", data?.message);
          return;
        }
        const rows = Array.isArray(data.data?.lead_info) ? data.data.lead_info : [];
        setLeads((prev) => [...prev, ...rows.map(toLeadCard)]);
        setLeadsPage(nextPage);
        setHasMoreLeads(!!data.nextpage);
      })
      .catch((err) => {
        if (mySeq !== requestSeqRef.current) return;
        console.error("get_leads (load more) failed", err.response?.status, err.response?.data || err.message);
      })
      .finally(() => {
        if (mySeq === requestSeqRef.current) setLoadingMoreLeads(false);
      });
  };

  return {
    leads,
    leadsLoading,
    hasMoreLeads,
    loadingMoreLeads,
    leadsTotal,
    loadMoreLeads,
    tileBuckets,
    q,
    setQ,
    filter,
    setFilter,
  };
}
